import React, { useState } from 'react';
import styled from 'styled-components';
import { FaCode, FaArrowUp, FaArrowDown, FaTrash, FaSmile } from 'react-icons/fa';
import { fabric } from 'fabric';

const shortcodes = [
  { label: 'FirstName', value: '{{firstName}}' },
  { label: 'LastName', value: '{{lastName}}' },
  { label: 'Email', value: '{{email}}' },
  { label: 'CompanyName', value: '{{companyName}}' },
  { label: 'Country', value: '{{Country}}' },
  { label: 'Title', value: '{{Title}}' },
  { label: 'Date', value: '{{date}}' },
  { label: 'Time', value: '{{time}}' }
];

const WidgetContainer = styled.div<{ $visible: boolean; $left: number; $top: number }>`
  position: absolute;
  left: ${props => props.$left}px;
  top: ${props => props.$top}px;
  transform: translateX(-50%);
  display: ${props => props.$visible ? 'flex' : 'none'};
  align-items: center;
  gap: 2px;
  padding: 3px 4px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.04);
  z-index: 1000;
  color: #1f2937;
  user-select: none;

  .dark & {
    background: rgba(31, 41, 55, 0.95);
    border-color: rgba(255, 255, 255, 0.08);
    color: #e5e7eb;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.04);
  }
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 26px;
  cursor: grab;
  border-radius: 4px;
  color: #9ca3af;
  flex-shrink: 0;
  transition: color 0.15s;

  &:active {
    cursor: grabbing;
  }

  &:hover {
    color: #6b7280;
  }

  .dark & {
    color: #6b7280;
    &:hover {
      color: #9ca3af;
    }
  }
`;

const ShortcodeSelect = styled.select`
  height: 28px;
  padding: 0 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #ffffff;
  color: #1f2937;
  font-size: 13px;
  cursor: pointer;
  outline: none;

  &:focus {
    border-color: #4285f4;
  }

  .dark & {
    background: #374151;
    border-color: #4b5563;
    color: #e5e7eb;
  }
`;

const ControlButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.08);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .dark & {
    color: #e5e7eb;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  }
`;

const EmojiButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;

  &:hover {
    background: rgba(0, 0, 0, 0.08);
  }

  .dark & {
    color: #e5e7eb;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  }
`;

const EmojiDropdown = styled.div`
  position: absolute;
  top: 36px;
  right: 0;
  width: 250px;
  max-height: 250px;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1001;

  .dark & {
    background: #1f2937;
    border-color: #4b5563;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }
`;

const EmojiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 6px;
`;

const EmojiOption = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.08);
  }

  .dark &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const popularEmojis = [
  '😀','😁','😂','🤣','😊','😍','😎','😢','😭','😡','😱','😴','🤤','😋','🥳','😇','🤗','🤔','😏','😌',
  '😮','😯','😲','😳','🥺','😤','😨','😰','😥','🤯','😵','🥴','🤢','🤮','🤧','😷','🤒','🤕','😪','😵‍💫',
  '👍','👎','👏','🙏','👋','🤝','✌️','🤞','🤟','🤘','👌','🤌','🤏','✊','👊','🤛','🤜','💪','🦾','🦿',
  '👈','👉','👆','👇','☝️','🤚','🖐️','✋','🖖',
  '🔥','🚀','🎉','💡','✅','❤️','💯','⭐','🌟','✨','💫','💥','💢','💤','💨','💦','💧','🌊','☀️','🌙',
  '🌈','☁️','⛈️','🌧️','❄️','☃️','⛄','🌨️','🌩️','🌪️','🌀','☔','⛅','🌤️','🌦️',
  '🎈','🎁','🎊','🎀','🎂','🍰','🧁','🎆','🎇',
  '🍕','🍔','🍟','🌮','🌯','🍱','🍣','🍤','🍙','🍚','🍛','🍜','🍝','🍠','🍢','🍥','🥮','🍡','🥟','🥠',
  '🥡','🍦','🍧','🍨','🍩','🍪','🥧','🍫','🍬','🍭','🍮','🍯','🍼','🥛','☕','🍵','🍶','🍷','🍸','🍹',
  '🍺','🍻','🥂','🥃','🥤','🧃','🧉','🧊','🥢',
  '🏀','⚽','🏈','⚾','🎾','🏐','🏉','🎱','🏓','🏸','🥅','🏒','🏑','🏏','🥍','🏹','🎣','🥊','🥋','🎽',
  '🏋️','🤸','🤼','🤾','🤺','🏌️','🏇','🧘','🏄','🏊',
  '🎮','🕹️','🎯','🎲','🎨','🎭','🎪','🎬','🎤','🎧','🎼','🎹','🥁','🎷','🎺','🎸','🪕','🎻','♟️','🎳',
  '🎰','🖼️','🖌️','🖍️','✏️','✒️','🖊️','🖋️','📝',
  '💼','📁','📂','🗂️','📅','📆','🗒️','🗓️','📇','📈','📉','📊','📋','📌','📍','📎','🖇️','📏','📐',
  '✂️','🗑️','🔒','🔓','🔑','🗝️','🔨','🪓','⛏️','⚒️','🛠️',
  '💻','⌨️','🖥️','🖨️','🖱️','🖲️','💾','💿','📀','📱','📞','☎️','📟','📠','📺','📻','🎙️','🎚️','🎛️',
  '🧭','⏱️','⏲️','⏰','🕰️','⌛','⏳','📡','🔬','🔭','🧪','🧫','⚗️',
  '🐶','🐱','🐭','🐹','🐰','🦊','🐻','🐼','🐨','🐯','🦁','🐮','🐷','🐽','🐸','🐵','🙈','🙉','🙊','🐒',
  '🐔','🐧','🐦','🐤','🐣','🐥','🦆','🦅','🦉','🦇','🐺','🐗','🐴','🦄','🐝','🐛','🦋','🐌','🐞','🐜',
  '🦟','🦗','🕷️','🦂','🐢','🐍','🦎','🦖','🦕','🐙','🦑','🦐','🦞','🦀','🐡','🐠','🐟','🐬','🐳','🐋',
  '🦈','🐊','🐅','🐆','🦓','🦍','🦧','🐘','🦛','🦏','🐪','🐫','🦒','🦘','🦬','🐃','🐂','🐄','🐎','🐖',
  '🐏','🐑','🦙','🐐','🦌','🐕','🐩','🦮','🐕‍🦺','🐈','🐈‍⬛','🪶','🦩','🦚','🦜','🐓','🦃',
  '🚗','🚕','🚙','🚌','🚎','🏎️','🚓','🚑','🚒','🚐','🛻','🚚','🚛','🚜','🏍️','🛵','🚲','🛴','🛹','🛼',
  '🚁','🛸','✈️','🛩️','🛫','🛬','🪂','💺','🚢','⛵','🛥️','🛳️','⛴️','🚤','🛶','🪝','⛽','🚨','🚥','🚦',
  '🚧','🛑','⚓','🏠','🏡','🏘️','🏚️','🏗️','🏭','🏢','🏬','🏣','🏤','🏥','🏦','🏨','🏪','🏫','🏩','💒',
  '🏛️','⛪','🕌','🕍','🕋','⛩️','🛕','🛖','🏜️','🏝️','🏞️','🏟️','🧱','🪨','🪵','🌍','🌎','🌏','🌐','🗺️',
  '🏔️','⛰️','🌋','🗻','🏕️','🏖️','🌉','🌁','🌃','🌆','🌇','♨️','🎠','🎡','🎢','💈','🚂','🚃','🚄','🚅',
  '🚆','🚇','🚈','🚉','🚊','🚝','🚞','🚋',
  '💎','🔮','🪄','🧿','🪬','⚱️','🗿','🪧','🪪','💌','💍','🔇','🔈','🔉','🔊','📢','📣','📯','🔔','🔕',
  '📻','💬','💭','🗯️','💢','🕳️','💣','👁️‍🗨️','🗨️'
];

interface ObjectControlsProps {
  canvas: fabric.Canvas | null;
  selectedObject: fabric.Object | null;
}

const ObjectControls: React.FC<ObjectControlsProps> = ({ canvas, selectedObject }) => {
  const [selectedShortcode, setSelectedShortcode] = useState('');
  const [position, setPosition] = useState({ left: 0, top: 0 });
  const [dragOffset, setDragOffset] = useState<{ dx: number; dy: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const emojiButtonRef = React.useRef<HTMLButtonElement>(null);
  const dragRef = React.useRef<{ startX: number; startY: number; origDx: number; origDy: number } | null>(null);
  const prevObjectRef = React.useRef<fabric.Object | null>(null);

  const isIText = (obj: fabric.Object): obj is fabric.IText => {
    return obj.type === 'i-text' || obj.type === 'textbox';
  };

  // Reset drag offset when a different object is selected
  React.useEffect(() => {
    if (selectedObject !== prevObjectRef.current) {
      setDragOffset(null);
      prevObjectRef.current = selectedObject;
    }
  }, [selectedObject]);

  React.useEffect(() => {
    if (!canvas || !selectedObject) return;

    const updatePosition = () => {
      if (isDragging) return;
      const zoom = canvas.getZoom();
      const boundingRect = selectedObject.getBoundingRect();
      const canvasRect = canvas.getElement().getBoundingClientRect();
      const dx = dragOffset?.dx || 0;
      const dy = dragOffset?.dy || 0;

      setPosition({
        left: (boundingRect.left + boundingRect.width / 2) * zoom + canvasRect.left + dx,
        top: boundingRect.top * zoom + canvasRect.top - 45 + dy
      });
    };

    updatePosition();
    canvas.on('object:modified', updatePosition);
    canvas.on('object:moving', updatePosition);

    return () => {
      canvas.off('object:modified', updatePosition);
      canvas.off('object:moving', updatePosition);
    };
  }, [canvas, selectedObject, dragOffset, isDragging]);

  const handleDragStart = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const currentOffset = dragOffset || { dx: 0, dy: 0 };
    dragRef.current = { startX: e.clientX, startY: e.clientY, origDx: currentOffset.dx, origDy: currentOffset.dy };
    setIsDragging(true);

    const handleDragMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = ev.clientX - dragRef.current.startX + dragRef.current.origDx;
      const dy = ev.clientY - dragRef.current.startY + dragRef.current.origDy;
      setDragOffset({ dx, dy });

      // Update position immediately during drag
      if (canvas && selectedObject) {
        const zoom = canvas.getZoom();
        const boundingRect = selectedObject.getBoundingRect();
        const canvasRect = canvas.getElement().getBoundingClientRect();
        setPosition({
          left: (boundingRect.left + boundingRect.width / 2) * zoom + canvasRect.left + dx,
          top: boundingRect.top * zoom + canvasRect.top - 45 + dy
        });
      }
    };

    const handleDragEnd = () => {
      dragRef.current = null;
      setIsDragging(false);
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
    };

    window.addEventListener('mousemove', handleDragMove);
    window.addEventListener('mouseup', handleDragEnd);
  }, [dragOffset, canvas, selectedObject]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiButtonRef.current && !emojiButtonRef.current.contains(event.target as Node)) {
        setIsEmojiPickerOpen(false);
      }
    };

    if (isEmojiPickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEmojiPickerOpen]);

  const handleShortcodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (!value || !selectedObject || !isIText(selectedObject) || !canvas) return;

    const text = selectedObject.text || '';
    const cursorPosition = selectedObject.selectionStart || 0;
    const newText = text.slice(0, cursorPosition) + value + text.slice(cursorPosition);
    
    selectedObject.set('text', newText);
    selectedObject.selectionStart = cursorPosition + value.length;
    selectedObject.selectionEnd = cursorPosition + value.length;
    canvas.renderAll();
    setSelectedShortcode('');
  };

  const insertAtCursor = (value: string) => {
    if (!value || !selectedObject || !isIText(selectedObject) || !canvas) return;

    const text = selectedObject.text || '';
    const cursorPosition = selectedObject.selectionStart ?? text.length;
    const newText = text.slice(0, cursorPosition) + value + text.slice(cursorPosition);

    selectedObject.set('text', newText);
    selectedObject.selectionStart = cursorPosition + value.length;
    selectedObject.selectionEnd = cursorPosition + value.length;
    canvas.renderAll();
  };

  const handleEmojiClick = (emoji: string) => {
    insertAtCursor(emoji);
    setIsEmojiPickerOpen(false);
  };

  const handleBringToFront = () => {
    if (!selectedObject || !canvas) return;
    selectedObject.bringToFront();
    canvas.renderAll();
  };

  const handleSendToBack = () => {
    if (!selectedObject || !canvas) return;
    selectedObject.sendToBack();
    canvas.renderAll();
  };

  const handleDelete = () => {
    if (!selectedObject || !canvas) return;
    canvas.remove(selectedObject);
    canvas.renderAll();
  };

  if (!selectedObject) return null;

  return (
    <WidgetContainer $visible={true} $left={position.left} $top={position.top}>
      <DragHandle onMouseDown={handleDragStart} title="Drag to move">
        <svg width="6" height="14" viewBox="0 0 6 14" fill="currentColor">
          <circle cx="1.5" cy="1.5" r="1"/>
          <circle cx="4.5" cy="1.5" r="1"/>
          <circle cx="1.5" cy="5.5" r="1"/>
          <circle cx="4.5" cy="5.5" r="1"/>
          <circle cx="1.5" cy="9.5" r="1"/>
          <circle cx="4.5" cy="9.5" r="1"/>
          <circle cx="1.5" cy="13.5" r="1"/>
          <circle cx="4.5" cy="13.5" r="1"/>
        </svg>
      </DragHandle>
      {isIText(selectedObject) && (
        <>
          <FaCode size={14} />
          <ShortcodeSelect
            value={selectedShortcode}
            onChange={handleShortcodeChange}
          >
            <option value="">Select a shortcode...</option>
            {shortcodes.map(shortcode => (
              <option key={shortcode.value} value={shortcode.value}>
                {shortcode.label}
              </option>
            ))}
          </ShortcodeSelect>
          <EmojiButton
            type="button"
            onClick={() => setIsEmojiPickerOpen(prev => !prev)}
            ref={emojiButtonRef}
            title="Insert emoji"
          >
            <FaSmile size={14} />
            {isEmojiPickerOpen && (
              <EmojiDropdown>
                <EmojiGrid>
                  {popularEmojis.map((emoji) => (
                    <EmojiOption
                      key={emoji}
                      type="button"
                      onClick={() => handleEmojiClick(emoji)}
                    >
                      {emoji}
                    </EmojiOption>
                  ))}
                </EmojiGrid>
              </EmojiDropdown>
            )}
          </EmojiButton>
        </>
      )}
      <ControlButton onClick={handleBringToFront} title="Bring to Front">
        <FaArrowUp size={14} />
      </ControlButton>
      <ControlButton onClick={handleSendToBack} title="Send to Back">
        <FaArrowDown size={14} />
      </ControlButton>
      <ControlButton onClick={handleDelete} title="Delete Object">
        <FaTrash size={14} />
      </ControlButton>
    </WidgetContainer>
  );
};

export default ObjectControls; 